const ATTRIBUTES_ROOT = '/attributes';
const WRAPPER_SEGMENT = '/attributes/domainEntityAttributes';

/**
 * Convert a JSON:API-style JSON Pointer (e.g. `/data/attributes/foo`) into
 * the JSON-Forms / Ajv `instancePath` shape used inside the form's
 * validation state (e.g. `/attributes/foo`).
 *
 * Ajv's `error.instancePath` is rooted at the data object passed to
 * `validate()` — for the entity form that's `entityRootData`, whose
 * top-level keys are `attributes`, `relationships`, etc.
 *
 * Two transformations are applied:
 * 1. Strip the `/data` prefix to align with Ajv's root.
 * 2. Strip the optional `domainEntityAttributes` wrapper. The
 *    config-app-backend stores form data under
 *    `attributes.domainEntityAttributes.*`, but `useEntityDetails`
 *    injects that wrapper itself when resolving the form's data slice
 *    — JSON Forms never sees it. Stripping here stays symmetric with
 *    `jsonPointerToFormPath` so backend pointers from both
 *    config-app-backend and non-config-app-backend entities map onto
 *    the same instance-path shape regardless of which side emits the
 *    wrapper.
 *
 * Returns `undefined` when the pointer is missing, doesn't anchor at
 * `/data/`, or collapses to no actual field segment beyond
 * `/attributes` (e.g. `/data/attributes`,
 * `/data/attributes/domainEntityAttributes/`, etc.). Trailing slashes
 * on otherwise-valid pointers are stripped so we never emit a
 * non-canonical instance path. Callers should treat `undefined` as
 * "no usable instance path" — the error can still surface in the
 * banner via `path`, just not inline on a form control.
 */
export const jsonPointerToInstancePath = (
  pointer: string | undefined,
): string | undefined => {
  if (!pointer || !pointer.startsWith('/data/')) return undefined;
  const afterData = pointer.slice('/data'.length);

  // Resolve to an instance path (with the optional wrapper stripped).
  // Wrapper match must end at a path boundary so a literal property
  // whose name starts with `domainEntityAttributes` isn't accidentally
  // truncated.
  let instancePath: string;
  if (afterData === WRAPPER_SEGMENT) {
    return undefined;
  }
  if (afterData.startsWith(`${WRAPPER_SEGMENT}/`)) {
    instancePath = `/attributes${afterData.slice(WRAPPER_SEGMENT.length)}`;
  } else {
    instancePath = afterData;
  }

  // Trim a trailing `/` so the path is canonical (matches Ajv's own
  // shape, which never includes trailing slashes).
  if (instancePath.endsWith('/')) {
    instancePath = instancePath.slice(0, -1);
  }

  // Reject pointers that collapse to no field beyond `/attributes` —
  // they don't refer to a specific control. Mirrors
  // `jsonPointerToFormPath`'s "no navigable segment" guard.
  if (instancePath === ATTRIBUTES_ROOT) return undefined;

  return instancePath;
};
